import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  PlusCircle,
  FileText,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Printer,
  Search,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const PurchasesList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);

  // Pagination state for DataTable
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });


  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => permissions.includes(permission);
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // Purchases Query
  const { data: purchaseOrderData, isLoading: purchasesLoading } = useQuery({
    queryKey: [
      "purchases",
      pagination.pageIndex + 1,
      pagination.pageSize,
      searchText,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.ORDERS, {
        params: {
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          search: searchText,
          with: ["items.product", "supplier", "store"],
        },
      });
      return res.data;
    },
  });

  const rawPurchases = purchaseOrderData?.data;
  const purchases = useMemo(() => rawPurchases || [], [rawPurchases]);

  const totalAmount = useMemo(() => {
    return purchases.reduce(
      (sum, p) => sum + parseFloat(p.total_amount || 0),
      0,
    );
  }, [purchases]);

  const meta = purchaseOrderData?.meta || {};
  const pageCount = meta.last_page || 1;

  // Use items directly from selected purchase
  const selectedPurchaseItems = selectedPurchase?.items || [];
  const itemsLoading = false;

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.PURCHASES.ORDER_DETAILS(id));
    },
    onSuccess: () => {
      toast.success("Purchase order deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete purchase order",
      );
    },
  });

  const fetchPurchaseOrderItems = (purchaseOrder) => {
    setSelectedPurchase(purchaseOrder);

    setIsModalVisible(true);
  };

  const handleGeneratePdf = async (id) => {
    toast.info("Generating Purchase Order PDF...");

    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.PURCHASE_ORDER_REPORT(id),
        {
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setShowPdfModal(true);
        toast.success("Purchase Order PDF generated successfully!");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Failed to generate Purchase Order PDF",
      );
    }
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `purchase-order-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully!");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      toast.info("Opening print dialog...");
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "tracking_number",
        header: "Tracking No.",
      },
      {
        accessorFn: (row) => row.supplier?.name,
        header: "Supplier",
      },
      {
        accessorFn: (row) => row.store?.name,
        header: "Store",
      },
      {
        accessorKey: "order_date",
        header: "Order Date",
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`,
      },
      {
        accessorKey: "paid",
        header: "Paid",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`,
      },
      {
        accessorFn: (row) => row.employee?.full_name,
        header: "Created By",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          const variants = {
            completed: "success",
            approved: "success",
            received: "success",
            sent: "info",
            cancelled: "destructive",
            canceled: "destructive",
            draft: "secondary",
            partial: "info",
            pending: "warning",
          };

          return (
            <Badge
              variant={variants[status] || "default"}
              className="capitalize font-medium"
            >
              {status?.replace(/_/g, " ")}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const purchase = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("purchase order read") && (
                  <DropdownMenuItem
                    onClick={() => fetchPurchaseOrderItems(purchase)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                )}
                {hasPermission("purchase order update") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        all_routes.editPurchase.replace(":id", purchase.id),
                      )
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("report purchase order view") && (
                  <DropdownMenuItem
                    onClick={() => handleGeneratePdf(purchase.id)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Download PDF
                  </DropdownMenuItem>
                )}
                {hasPermission("purchase order delete") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => setDeleteConfirmationId(purchase.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol, navigate],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Purchase Orders"
          subtitle="Manage your purchase orders and incoming stock"
        />
        <PageHeaderActions>
          {hasPermission("purchase order create") && (
            <Button onClick={() => navigate(all_routes.newPurchase)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Purchase Order
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search purchases..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={purchases}
          enableRowSelection={true}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={purchasesLoading}
          onRowClick={(row) => fetchPurchaseOrderItems(row)}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Orders: {meta.total || 0}
              </span>
              {totalAmount > 0 && (
                <span className="font-medium text-foreground">
                  Total Amount: {currencySymbol}
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              )}
            </div>
          }
        />
      </TableCard>

      {/* Delete Confirmation Alert */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              purchase order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteConfirmationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Details Dialog */}
      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Purchase Order Details - {selectedPurchase?.tracking_number}
            </DialogTitle>
            <DialogDescription className="px-5">
              Detailed overview of the selected purchase order and its items.
            </DialogDescription>
          </DialogHeader>

          {selectedPurchase && (
            <div className="space-y-6 px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Order Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Supplier:</span>
                      <span className="font-medium">
                        {selectedPurchase.supplier?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Store:</span>
                      <span className="font-medium">
                        {selectedPurchase.store?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order Date:</span>
                      <span className="font-medium">
                        {formatDate(selectedPurchase.order_date)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Due Date:</span>
                      <span className="font-medium">
                        {formatDate(selectedPurchase.due_date)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      {(() => {
                        const variants = {
                          completed: "success",
                          approved: "success",
                          received: "success",
                          sent: "info",
                          cancelled: "destructive",
                          canceled: "destructive",
                          draft: "secondary",
                          partial: "info",
                          pending: "warning",
                        };
                        return (
                          <Badge
                            variant={
                              variants[selectedPurchase.status] || "default"
                            }
                            className="capitalize font-medium"
                          >
                            {selectedPurchase.status?.replace(/_/g, " ")}
                          </Badge>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Sub Total:</span>
                      <span>
                        {currencySymbol}
                        {parseFloat(selectedPurchase.sub_total || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Discount:</span>
                      <span className="text-red-500">
                        -{currencySymbol}
                        {parseFloat(selectedPurchase.discount || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Order Tax:</span>
                      <span>
                        {currencySymbol}
                        {parseFloat(selectedPurchase.total_tax || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shipping:</span>
                      <span>
                        {currencySymbol}
                        {parseFloat(selectedPurchase.shipping || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t mt-2">
                      <span className="font-bold text-foreground">
                        Grand Total:
                      </span>
                      <span className="font-bold text-primary text-lg">
                        {currencySymbol}
                        {parseFloat(selectedPurchase.total || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Paid Amount:</span>
                      <span>
                        {currencySymbol}
                        {parseFloat(selectedPurchase.paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600 font-bold">
                      <span>Balance Due:</span>
                      <span>
                        {currencySymbol}
                        {(
                          parseFloat(selectedPurchase.total || 0) -
                          parseFloat(selectedPurchase.paid || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Items Table */}
              {hasPermission("purchase order item read") && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Order Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr className="border-b">
                            <th className="py-2 px-3 text-left font-medium">
                              Product
                            </th>
                            <th className="py-2 px-3 text-center font-medium">
                              Qty
                            </th>
                            <th className="py-2 px-3 text-right font-medium">
                              Unit Cost
                            </th>
                            <th className="py-2 px-3 text-center font-medium">
                              Discount
                            </th>
                            <th className="py-2 px-3 text-center font-medium">
                              Tax
                            </th>
                            <th className="py-2 px-3 text-right font-medium">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {itemsLoading ? (
                            <tr>
                              <td colSpan="6" className="py-4 text-center">
                                Loading items...
                              </td>
                            </tr>
                          ) : selectedPurchaseItems &&
                            selectedPurchaseItems.length > 0 ? (
                            selectedPurchaseItems.map((item, idx) => (
                              <tr key={idx}>
                                <td className="py-2 px-3">
                                  <div>
                                    <div className="font-medium text-foreground flex items-center gap-2">
                                      {item.product?.name || "Unknown"}
                                      {item.is_foc && (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] bg-green-50 text-green-700 border-green-200 h-4 px-1"
                                        >
                                          FOC
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                      SKU: {item.product?.sku || "N/A"}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {item.ordered_qty}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  {currencySymbol}
                                  {parseFloat(item.unit_cost || 0).toFixed(2)}
                                </td>
                                <td className="py-2 px-3 text-center text-red-500">
                                  {item.is_foc ? (
                                    <span className="text-[10px] bg-green-50 text-green-600 px-1 rounded">
                                      -100%
                                    </span>
                                  ) : parseFloat(
                                    item.discount_amount ||
                                    item.discount ||
                                    0,
                                  ) > 0 ? (
                                    <div className="flex flex-col items-center">
                                      <span className="text-xs">
                                        -{currencySymbol}
                                        {parseFloat(
                                          item.discount_amount || item.discount,
                                        ).toFixed(2)}
                                      </span>
                                      {item.discount_type === "percentage" && (
                                        <span className="text-[10px] text-muted-foreground">
                                          ({item.discount_value}%)
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <div>
                                    {currencySymbol}
                                    {parseFloat(item.tax || 0).toFixed(2)}
                                    <div className="text-[10px] text-muted-foreground capitalize">
                                      ({item.tax_method})
                                    </div>
                                  </div>
                                </td>
                                <td className="py-2 px-3 text-right font-medium text-primary">
                                  {currencySymbol}
                                  {parseFloat(item.total || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="6"
                                className="py-4 text-center text-muted-foreground"
                              >
                                No items found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {(selectedPurchase.internal_notes ||
                selectedPurchase.public_notes) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedPurchase.internal_notes && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm font-semibold">
                            Internal Notes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div
                            className="text-xs text-muted-foreground prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                selectedPurchase.internal_notes || "",
                              ),
                            }}
                          />
                        </CardContent>
                      </Card>
                    )}
                    {selectedPurchase.public_notes && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm font-semibold">
                            Public Notes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div
                            className="text-xs text-muted-foreground prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                selectedPurchase.public_notes || "",
                              ),
                            }}
                          />
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
            </div>
          )}
          <DialogFooter className="px-5 pb-5">
            <Button
              onClick={() => setIsModalVisible(false)}
              variant="secondary"
            >
              Close
            </Button>
            {selectedPurchase && hasPermission("report purchase order view") && (
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  handleGeneratePdf(selectedPurchase.id);
                }}
              >
                <FileText className="mr-2 h-4 w-4" /> Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Ready Dialog */}
      <Dialog open={showPdfModal} onOpenChange={handleClosePdfModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PDF Generated Successfully</DialogTitle>
            <DialogDescription>
              Your document is ready for download or printing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 space-y-4">
            <div className="text-green-600">
              <FileText size={48} />
            </div>
            <p className="text-center text-muted-foreground">
              Your Purchase Order PDF is ready to download or print.
            </p>
            <div className="flex gap-4">
              <Button onClick={handleDownloadPDF}>
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button variant="secondary" onClick={handlePrintPDF}>
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default PurchasesList;
